<template>
  <TornadoChart
    :x-unit="chartingData?.tdh.targetXUnit"
    reverse-y
    x-label="In Situ Flow Rate"
    :y-label="yLabel"
    :frequency-data-sets="chartingData?.tdh.operatingDataSets || []"
    :ror-data-sets="chartingData?.tdh.rorDataSets || []"
    :additional-data-sets="
      chartingData ? [chartingData.tdh.analysisDataSet] : []
    "
    :operating-points="operatingPoints"
    :x-legend-label="xLegendLabel"
    :y-legend-label="yLegendLabel"
  />
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import type { Scenario } from "@/composables/forms/formTypes";
import TornadoChart from "./TornadoChart.vue";
import { usePumpChartingData } from "@/libs/charting";
import type { PumpTornadoChartViewData } from "@/libs/charting";
import type { OperatingPoint } from "@/components/types";
import {
  scenarioCalculationsKey,
  scenarioKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import {
  PoundsPerSquareInchDifferential,
  PressureUnits
} from "@/libs/units/pressure";

const props = defineProps<{
  minFrequency: number;
  maxFrequency: number;
  scenario?: Scenario | null;
  scenarioCalculations?: ScenarioCalculations;
  xLegendLabel?: string;
  yLegendLabel?: string;
  pumpTornadoChartData?: PumpTornadoChartViewData;
}>();

const _scenario = props.scenario || injectStrict(scenarioKey);
const _sizing = injectStrict(sizingKey, null);
const _scenarioCalculations =
  props.scenarioCalculations || injectStrict(scenarioCalculationsKey);

const {
  getLiquidFlowUnit,
  getHydraulicPowerUnit,
  getLargeLengthUnit,
  getPressureUnit
} = useScenarioUnits(_scenario, _sizing);

const targetLiquidFlowUnit = getLiquidFlowUnit(
  "Target Liquid Rate.write.STB/D"
);
const targetPowerUnit = getHydraulicPowerUnit("Shaft Limit.convert.hp");
const targetLargeLengthUnit = getLargeLengthUnit("Required TDH.convert.Feet");
const targetPressureUnit = getPressureUnit(
  "Producing Bottomhole Pressure at Datum [Pwf].write.PSIA"
);

const chartingData = computed(() => {
  if (!props.pumpTornadoChartData) {
    return usePumpChartingData(
      props.minFrequency,
      props.maxFrequency,
      _scenarioCalculations,
      targetLiquidFlowUnit.value,
      targetPowerUnit.value,
      targetLargeLengthUnit.value,
      targetPressureUnit.value
    );
  } else {
    return props.pumpTornadoChartData;
  }
});

const targetYUnit = computed(() => {
  // If it's an HPS scenario, the TDH is actually in pressure units, so use that unit for the y axis
  return chartingData.value?.tdh.targetYUnit;
});

const operatingPoints = computed<OperatingPoint[]>(() =>
  (chartingData.value?.tdh.operatingPoints || []).map(point => ({
    ...point,
    hideYDetails: point.label === "Intake" || point.label === "Average"
  }))
);

const yLabel = computed(() => {
  const yUnit = targetYUnit.value;
  const isYUnitPSI =
    yUnit === PressureUnits.PoundsPerSquareInchAbsolute ||
    yUnit === PressureUnits.PoundsPerSquareInchGauge;
  if (_scenarioCalculations.isScenarioEspShore.value) {
    return `Total Dynamic Head (${
      targetLargeLengthUnit.value.inSituLabel ||
      targetLargeLengthUnit.value.shortLabel ||
      targetLargeLengthUnit.value.label
    })`;
  } else if (!isYUnitPSI) {
    return `Differential Pressure (${
      targetPressureUnit.value.inSituLabel ||
      targetPressureUnit.value.shortLabel ||
      targetPressureUnit.value.label
    })`;
  } else {
    return `Differential Pressure (${
      PoundsPerSquareInchDifferential.inSituLabel ||
      PoundsPerSquareInchDifferential.shortLabel ||
      PoundsPerSquareInchDifferential.label
    })`;
  }
});
</script>
